import { notificationsService } from '@datacloud-incubation/frontend-notifications-module';
import axios from 'axios';
import { firstValueFrom } from 'rxjs';
import {
  CONTENT_TYPE,
  HTTP_HEADERS,
  HTTP_RESPONSE_STATUS
} from '@core/constants/apiService.constant';
import { URL } from '@core/constants/url.constant';
import { Singleton } from '@core/decorators/Singleton.decorator';
import { refreshTokenMutation } from '@core/mutations/refreshToken.mutation';
import { environmentService } from './Environment.service';
import { errorService } from './Error.service';
import { tanstackQueryService } from './TanstackQuery.service';
import { tokenService } from './Token.service';
import type {
  TGet,
  TDelete,
  TPatch,
  TPost,
  TPut,
  InternalCustomConfig
} from '@core/models/apiService.model';
import type { TResponseError } from 'AbcloudzFlowChat/models/abcloudzFlowChat.model';
import type { AxiosInstance, AxiosError } from 'axios';

@Singleton
class ApiService {
  private readonly axiosInstance: AxiosInstance;
  private isRefreshingToken = false;
  private refreshSubscribers: VoidFunction[] = [];

  public constructor() {
    this.axiosInstance = axios.create({
      baseURL: environmentService.baseUrl,
      headers: {
        [HTTP_HEADERS.ContentType]: CONTENT_TYPE.JSON,
        [HTTP_HEADERS.Accept]: CONTENT_TYPE.JSON
      },
      timeout: 60000
    });

    this.axiosInstance.interceptors.request.use(
      (request) => {
        if (tokenService.getAccessToken) {
          request.headers.Authorization = `Bearer ${tokenService.getAccessToken}`;
        }

        return request;
      },

      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalCustomConfig;

        if (
          error.response &&
          error.response.status >= HTTP_RESPONSE_STATUS.badRequest &&
          originalRequest?.url === URL.tokenRefresh
        ) {
          this.isRefreshingToken = false;
          tanstackQueryService.queryClient.clear();

          tokenService.removeTokens();
          throw new Error('Failed refresh token');
        }

        if (
          originalRequest &&
          error.response?.status === HTTP_RESPONSE_STATUS.unauthorized &&
          originalRequest.url !== URL.abcloudzLogin
        ) {
          if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;
            try {
              await firstValueFrom(refreshTokenMutation.refreshToken$().observer$);
              this.isRefreshingToken = false;

              this.onRefreshSubscriber();
              return await this.axiosInstance(originalRequest);
            } catch (error) {
              this.isRefreshingToken = false;
              return Promise.reject(error as AxiosError);
            }
          }

          return new Promise((resolve) => {
            this.addSubscriber(() => {
              resolve(this.axiosInstance(originalRequest));
            });
          });
        }

        const serverData = error.response?.data as TResponseError | undefined;

        if (!originalRequest?.silent) {
          notificationsService.errorNotification(errorService.handleAxiosError(error));
        }

        if (serverData?.message) {
          error.message = serverData.message;
        }

        return Promise.reject(error);
      }
    );
  }

  private addSubscriber(cb: VoidFunction) {
    this.refreshSubscribers.push(cb);
  }

  private onRefreshSubscriber() {
    this.refreshSubscribers.forEach((cb) => {
      cb();
    });
    this.refreshSubscribers = [];
  }

  public get api() {
    return this.axiosInstance;
  }

  public get = <T>({ url, config }: TGet) => this.api.get<T>(url, config);

  public post = <T>({ url, config, dto }: TPost) => this.api.post<T>(url, dto, config);

  public patch = <T>({ url, config, dto }: TPatch) => this.api.patch<T>(url, dto, config);

  public put = <T>({ url, config, dto }: TPut) => this.api.put<T>(url, dto, config);

  public delete = <T>({ url, config }: TDelete) => this.api.delete<T>(url, config);
}

const apiService = new ApiService();

export { apiService };
